import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, AlertTriangle } from "lucide-react";
import { format, addDays, subDays, startOfWeek, addWeeks, subWeeks, startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, getDay } from "date-fns";

const HOURS_RRDC_WEEKDAY = { start: 15, end: 21 };
const HOURS_RRDC_WEEKEND = { start: 8, end: 21 };
const HOURS_TH_WEEKDAY = { start: 12, end: 22 };
const HOURS_TH_WEEKEND = { start: 7, end: 22 };

const STATE_STYLES = {
  tentative: { background: "repeating-linear-gradient(45deg, #3f3f46, #3f3f46 4px, #52525b 4px, #52525b 8px)", borderLeft: "4px solid #a1a1aa" },
  reserved: { background: "#854d0e", borderLeft: "4px solid #facc15" },
  booked: { background: "#7f1d1d", borderLeft: "4px solid #ef4444" },
  manual_block: { background: "#3f3f46", borderLeft: "4px solid #71717a" },
  external_busy: { background: "#7f1d1d", borderLeft: "4px solid #ef4444" },
};

const STATE_STYLES_LIGHT = {
  tentative: { background: "repeating-linear-gradient(45deg, #d4d4d8, #d4d4d8 4px, #e4e4e7 4px, #e4e4e7 8px)", borderLeft: "4px solid #71717a" },
  reserved: { background: "#fef3c7", borderLeft: "4px solid #d97706" },
  booked: { background: "#fee2e2", borderLeft: "4px solid #dc2626" },
  manual_block: { background: "#e4e4e7", borderLeft: "4px solid #71717a" },
  external_busy: { background: "#fee2e2", borderLeft: "4px solid #dc2626" },
};

const TYPE_LABEL_MAP = {
  rental: "Rental",
  field_rental: "Rental",
  birthday_party: "Birthday Party",
  picnic: "Picnic",
  event: "Event",
};

function blockLabel(block, includeLocation = false) {
  // Manual non-lead blocks → keep existing reason
  if (block.state === "manual_block") {
    const base = block.reason || "Blocked";
    return includeLocation ? `${locLabel(block.calendar)} · ${base}` : base;
  }
  // Tentative hold → identify it as a hold first
  const stateLabels = {
    tentative: "Tentative Hold",
    reserved: "Reserved",
    booked: "Booked",
    external_busy: "Booked (Google)",
  };
  let prefix = stateLabels[block.state] || block.reason || block.state?.replace("_", " ");
  // If we have a lead, show TYPE + name
  let suffix = "";
  if (block.lead_inquiry_type) {
    const t = block.lead_inquiry_type;
    let typeLabel = TYPE_LABEL_MAP[t] || t;
    if ((t === "rental" || t === "field_rental")) {
      typeLabel = block.lead_rental_subtype === "other" ? "Other Rental" : "Athletic Rental";
    }
    const name = `${block.lead_first_name || ""} ${block.lead_last_name || ""}`.trim();
    suffix = ` · ${typeLabel}${name ? ` — ${name}` : ""}`;
  } else if (block.state === "external_busy") {
    suffix = ` — ${block.title || "Google event"}`;
  }
  if (includeLocation) {
    return `${locLabel(block.calendar)} · ${prefix}${suffix}`;
  }
  return `${prefix}${suffix}`;
}

function locLabel(cal) {
  if (cal === "turf_house") return "Turf House";
  if (cal === "rrdc_field") return "Rolling River Field";
  if (cal === "rrdc_rink") return "Rolling River Rink";
  if (cal === "rrdc_courts") return "Rolling River Courts";
  return "";
}

// Seasonal blocks
const CAMP_SEASON_START = "2026-06-26";
const CAMP_SEASON_END = "2026-08-20";
const HOLIDAY_BLOCK_START = "2026-07-03";
const HOLIDAY_BLOCK_END = "2026-07-06";
const TH_CAMP_START = "2026-06-29";
const TH_CAMP_END = "2026-08-21";

function getOperatingHours(calendar, date) {
  const d = new Date(date + "T00:00:00");
  const day = d.getDay();
  const isWeekend = day === 0 || day === 6;

  // RRDC family — field, rink, courts all share Rolling River hours
  if (calendar === "rrdc_field" || calendar === "rrdc_rink" || calendar === "rrdc_courts") {
    // During camp season, weekdays restricted to 5PM-9PM
    if (date >= CAMP_SEASON_START && date <= CAMP_SEASON_END && !isWeekend) {
      return { start: 17, end: 21 };
    }
    return isWeekend ? HOURS_RRDC_WEEKEND : HOURS_RRDC_WEEKDAY;
  }
  return isWeekend ? HOURS_TH_WEEKEND : HOURS_TH_WEEKDAY;
}

function isHolidayBlocked(calendar, date) {
  const isRR = calendar === "rrdc_field" || calendar === "rrdc_rink" || calendar === "rrdc_courts";
  return isRR && date >= HOLIDAY_BLOCK_START && date <= HOLIDAY_BLOCK_END;
}

function isTHCampBlocked(calendar, date, time) {
  if (calendar !== "turf_house") return false;
  if (date < TH_CAMP_START || date > TH_CAMP_END) return false;
  const hour = parseInt(time.split(":")[0]);
  return hour >= 9 && hour < 12;
}

function generateSlots(startHour, endHour) {
  const slots = [];
  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += 15) {
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
}

const calendarLabel = (cal) => {
  if (cal === "turf_house") return "Turf House";
  if (cal === "rrdc_field") return "RR Field";
  if (cal === "rrdc_rink") return "RR Rink";
  if (cal === "rrdc_courts") return "RR Courts";
  return "All Locations";
};

export default function CalendarPage() {
  const { user } = useAuth();
  const [calendar, setCalendar] = useState("all");
  const [currentDate, setCurrentDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [view, setView] = useState("day");
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBlock, setShowBlock] = useState(false);
  const [blockForm, setBlockForm] = useState({
    start_time: "", end_time: "", reason: "",
    first_name: "", last_name: "", email: "", phone: "",
  });

  const isWalkIn = ["Field Rental (walk-in)", "Birthday Party (walk-in)", "Event (walk-in)", "School Picnic (walk-in)"].includes(blockForm.reason);

  // Compute overlapping blocks for the chosen date + start/end times (used for conflict warning)
  const conflictingBlocks = (() => {
    if (!showBlock || !blockForm.start_time || !blockForm.end_time) return [];
    if (blockForm.end_time <= blockForm.start_time) return [];
    return blocks.filter(
      (b) =>
        b.date === currentDate &&
        b.start_time < blockForm.end_time &&
        b.end_time > blockForm.start_time
    );
  })();

  const dateRange = useMemo(() => {
    if (view === "day") return { start: currentDate, end: currentDate };
    if (view === "week") {
      const d = new Date(currentDate + "T00:00:00");
      const ws = startOfWeek(d, { weekStartsOn: 1 });
      return { start: format(ws, "yyyy-MM-dd"), end: format(addDays(ws, 6), "yyyy-MM-dd") };
    }
    // month
    const d = new Date(currentDate + "T00:00:00");
    return { start: format(startOfMonth(d), "yyyy-MM-dd"), end: format(endOfMonth(d), "yyyy-MM-dd") };
  }, [currentDate, view]);

  useEffect(() => {
    setLoading(true);
    api.get(`/calendar/blocks?calendar=${calendar}&start_date=${dateRange.start}&end_date=${dateRange.end}`)
      .then(({ data }) => setBlocks(data.blocks))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [calendar, dateRange]);

  const navDate = (dir) => {
    const d = new Date(currentDate + "T00:00:00");
    if (view === "day") setCurrentDate(format(dir > 0 ? addDays(d, 1) : subDays(d, 1), "yyyy-MM-dd"));
    else if (view === "week") setCurrentDate(format(dir > 0 ? addWeeks(d, 1) : subWeeks(d, 1), "yyyy-MM-dd"));
    else setCurrentDate(format(dir > 0 ? addMonths(d, 1) : subMonths(d, 1), "yyyy-MM-dd"));
  };

  const hours = getOperatingHours(calendar === "all" ? "turf_house" : calendar, currentDate);
  // In "all" view, widen range to encompass both facilities
  const unifiedHours = calendar === "all"
    ? {
        start: Math.min(
          getOperatingHours("turf_house", currentDate).start,
          getOperatingHours("rrdc_field", currentDate).start,
        ),
        end: Math.max(
          getOperatingHours("turf_house", currentDate).end,
          getOperatingHours("rrdc_field", currentDate).end,
        ),
      }
    : hours;
  const slots = generateSlots(unifiedHours.start, unifiedHours.end);

  const getBlockForSlot = (date, time, cal = null) => {
    return blocks.find(
      (b) =>
        b.date === date &&
        b.start_time <= time &&
        b.end_time > time &&
        (cal ? b.calendar === cal : true)
    );
  };

  const handleCreateBlock = async () => {
    if (!blockForm.start_time || !blockForm.end_time) { toast.error("Select times"); return; }
    if (!blockForm.reason) { toast.error("Select a reason"); return; }
    if (blockForm.end_time <= blockForm.start_time) { toast.error("End time must be after start time"); return; }
    const targetCalendar = calendar === "all" ? blockForm.location : calendar;
    if (!targetCalendar || targetCalendar === "all") { toast.error("Pick a location"); return; }
    if (conflictingBlocks.length > 0 && !blockForm.confirm_override) {
      toast.error("This slot conflicts with existing bookings. Check 'Book anyway' to override.");
      return;
    }

    const walkin = ["Field Rental (walk-in)", "Birthday Party (walk-in)", "Event (walk-in)", "School Picnic (walk-in)"].includes(blockForm.reason);

    if (walkin) {
      if (!blockForm.first_name || !blockForm.last_name) { toast.error("Enter client name"); return; }
      // Create a lead which also creates the calendar block
      const typeMap = {
        "Field Rental (walk-in)": "rental",
        "Birthday Party (walk-in)": "birthday_party",
        "Event (walk-in)": "event",
        "School Picnic (walk-in)": "picnic",
      };
      try {
        await api.post("/leads", {
          inquiry_type: typeMap[blockForm.reason],
          location: targetCalendar === "turf_house" ? "turf_house" : "rrdc_field",
          first_name: blockForm.first_name,
          last_name: blockForm.last_name,
          email: blockForm.email,
          phone: blockForm.phone,
          requested_date: currentDate,
          start_time: blockForm.start_time,
          duration: Math.max(1, Math.round((parseInt(blockForm.end_time.split(":")[0]) * 60 + parseInt(blockForm.end_time.split(":")[1]) - parseInt(blockForm.start_time.split(":")[0]) * 60 - parseInt(blockForm.start_time.split(":")[1])) / 60 * 2) / 2),
          lead_source: "Walk-in",
        });
        toast.success("Lead created & calendar blocked");
      } catch { toast.error("Failed to create lead"); return; }
    } else {
      try {
        const force = blockForm.confirm_override ? "?force=1" : "";
        await api.post(`/calendar/blocks${force}`, {
          calendar: targetCalendar, date: currentDate,
          start_time: blockForm.start_time, end_time: blockForm.end_time,
          reason: blockForm.reason,
        });
        toast.success("Block created");
      } catch (err) {
        if (err.response?.status === 409) {
          toast.error("Time conflict — check 'Book anyway' to override");
        } else { toast.error("Failed to create block"); }
        return;
      }
    }

    setShowBlock(false);
    setBlockForm({ start_time: "", end_time: "", reason: "", first_name: "", last_name: "", email: "", phone: "", confirm_override: false });
    api.get(`/calendar/blocks?calendar=${calendar}&start_date=${dateRange.start}&end_date=${dateRange.end}`)
      .then(({ data }) => setBlocks(data.blocks));
  };

  const renderDayView = (date) => {
    // "All locations" — render 4 columns side-by-side
    if (calendar === "all") {
      const thHours = getOperatingHours("turf_house", date);
      const rrdcHours = getOperatingHours("rrdc_field", date);
      const rrdcBlocked = isHolidayBlocked("rrdc_field", date);
      // Rink and Courts share RRDC's hours / holiday rules
      return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { key: "turf_house",  label: "Turf House",     short: "TH",     accent: "bg-emerald-500", chipBg: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30", badgeBg: "bg-emerald-500 text-white", hours: thHours,   hol: false },
            { key: "rrdc_field",  label: "RR Field",       short: "FIELD",  accent: "bg-blue-500",    chipBg: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",          badgeBg: "bg-blue-500 text-white",    hours: rrdcHours, hol: rrdcBlocked },
            { key: "rrdc_rink",   label: "RR Rink",        short: "RINK",   accent: "bg-cyan-500",    chipBg: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30",          badgeBg: "bg-cyan-500 text-white",    hours: rrdcHours, hol: rrdcBlocked },
            { key: "rrdc_courts", label: "RR Courts",      short: "COURT",  accent: "bg-orange-500",  chipBg: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30",  badgeBg: "bg-orange-500 text-white",  hours: rrdcHours, hol: rrdcBlocked },
          ].map((col) => (
            <div key={col.key} data-testid={`day-col-${col.key}`}>
              <div className={`flex items-center justify-center gap-2 mb-3 py-2 rounded-md border ${col.chipBg}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${col.accent}`} />
                <span className="text-xs font-bold uppercase tracking-wide">{col.label}</span>
              </div>
              {col.hol ? (
                <div className="text-center py-8 text-red-400 font-medium text-xs">Holiday Block — closed July 3-6</div>
              ) : (
                <div className="space-y-px">
                  {generateSlots(col.hours.start, col.hours.end).map((slot) => {
                    const block = getBlockForSlot(date, slot, col.key);
                    const thCampBlock = isTHCampBlocked(col.key, date, slot);
                    const isStart = block && block.start_time === slot;
                    const isDark = document.documentElement.classList.contains("dark");
                    const styles = isDark ? STATE_STYLES : STATE_STYLES_LIGHT;
                    const blockStyle = block ? styles[block.state] || {} : thCampBlock ? styles.manual_block : {};
                    const isBlocked = block || thCampBlock;
                    return (
                      <div
                        key={`${col.key}-${date}-${slot}`}
                        data-testid={`slot-${col.key}-${date}-${slot}`}
                        className={`flex items-center h-8 px-2 text-xs rounded-r transition-colors ${!isBlocked ? "border-l-4 border-l-emerald-500/40 hover:bg-muted/50" : ""}`}
                        style={isBlocked ? { ...blockStyle, borderRadius: "0 4px 4px 0" } : {}}
                      >
                        <span className={`w-12 font-mono shrink-0 ${isBlocked ? "text-foreground/70" : "text-muted-foreground"}`}>{slot}</span>
                        {thCampBlock && !block && <span className="ml-2 truncate font-medium text-xs text-foreground/60">Sports Camp</span>}
                        {isStart && (
                          <span className="ml-2 truncate font-medium text-xs text-foreground flex items-center gap-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${col.badgeBg}`}>
                              {col.short}
                            </span>
                            <span className="truncate">{blockLabel(block)}</span>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }
    if (isHolidayBlocked(calendar, date)) {
      return <div className="text-center py-12 text-red-400 font-medium">Holiday Block — RRDC Field closed July 3-6, 2026</div>;
    }
    const dayHours = getOperatingHours(calendar, date);
    const daySlots = generateSlots(dayHours.start, dayHours.end);

    return (
      <div className="space-y-px">
        {daySlots.map((slot) => {
          const block = getBlockForSlot(date, slot);
          const thCampBlock = isTHCampBlocked(calendar, date, slot);
          const isStart = block && block.start_time === slot;
          const isDark = document.documentElement.classList.contains("dark");
          const styles = isDark ? STATE_STYLES : STATE_STYLES_LIGHT;
          const blockStyle = block ? styles[block.state] || {} : thCampBlock ? styles.manual_block : {};
          const isBlocked = block || thCampBlock;

          return (
            <div
              key={`${date}-${slot}`}
              data-testid={`slot-${date}-${slot}`}
              className={`flex items-center h-8 px-2 text-xs rounded-r transition-colors ${
                !isBlocked ? "border-l-4 border-l-emerald-500/40 hover:bg-muted/50" : ""
              }`}
              style={isBlocked ? { ...blockStyle, borderRadius: "0 4px 4px 0" } : {}}
            >
              <span className={`w-12 font-mono shrink-0 ${isBlocked ? "text-foreground/70" : "text-muted-foreground"}`}>{slot}</span>
              {thCampBlock && !block && <span className="ml-2 truncate font-medium text-xs text-foreground/60">Sports Camp</span>}
              {isStart && (
                <span className="ml-2 truncate font-medium text-xs text-foreground flex items-center gap-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${block.calendar === "turf_house" ? "bg-emerald-500 text-white" : "bg-blue-500 text-white"}`}>
                    {block.calendar === "turf_house" ? "TH" : "RRDC"}
                  </span>
                  <span className="truncate">{blockLabel(block)}</span>
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeekView = () => {
    const d = new Date(dateRange.start + "T00:00:00");
    const days = Array.from({ length: 7 }, (_, i) => format(addDays(d, i), "yyyy-MM-dd"));

    return (
      <div className="grid grid-cols-7 gap-2">
        {days.map((date) => {
          const dayBlocks = blocks.filter((b) => b.date === date);
          const dayName = format(new Date(date + "T00:00:00"), "EEE");
          const dayNum = format(new Date(date + "T00:00:00"), "d");
          const isToday = date === format(new Date(), "yyyy-MM-dd");

          return (
            <div key={date} className="space-y-1">
              <div
                className={`text-center py-1 text-xs font-medium rounded cursor-pointer hover:bg-muted/50 ${
                  isToday ? "bg-primary/10 text-primary" : "text-muted-foreground"
                }`}
                onClick={() => { setCurrentDate(date); setView("day"); }}
              >
                {dayName} <span className="font-bold">{dayNum}</span>
              </div>
              {dayBlocks.map((b) => {
                const isDark = document.documentElement.classList.contains("dark");
                const styles = isDark ? STATE_STYLES : STATE_STYLES_LIGHT;
                return (
                  <div
                    key={b.id}
                    className="text-xs p-1 rounded truncate"
                    style={styles[b.state] || {}}
                    title={`${b.start_time}-${b.end_time} · ${blockLabel(b, true)}`}
                  >
                    {b.start_time}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMonthView = () => {
    const d = new Date(currentDate + "T00:00:00");
    const monthStart = startOfMonth(d);
    const monthEnd = endOfMonth(d);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const startPad = getDay(monthStart) === 0 ? 6 : getDay(monthStart) - 1;
    const paddedDays = [...Array(startPad).fill(null), ...days];

    return (
      <div>
        <div className="grid grid-cols-7 gap-px mb-1">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => (
            <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px">
          {paddedDays.map((day, idx) => {
            if (!day) return <div key={`pad-${idx}`} className="h-24 bg-muted/20 rounded" />;
            const dateStr = format(day, "yyyy-MM-dd");
            const dayBlocks = blocks.filter(b => b.date === dateStr);
            const isToday = dateStr === format(new Date(), "yyyy-MM-dd");
            return (
              <div
                key={dateStr}
                className={`h-24 p-1 rounded border border-border/50 cursor-pointer hover:bg-muted/30 transition-colors ${isToday ? "bg-primary/5 border-primary/30" : ""}`}
                onClick={() => { setCurrentDate(dateStr); setView("day"); }}
              >
                <div className={`text-xs font-medium ${isToday ? "text-primary" : "text-foreground"}`}>{format(day, "d")}</div>
                <div className="space-y-0.5 mt-0.5">
                  {dayBlocks.slice(0, 3).map(b => {
                    const isDark = document.documentElement.classList.contains("dark");
                    const styles = isDark ? STATE_STYLES : STATE_STYLES_LIGHT;
                    // Pull short identifier
                    let who = "";
                    if (b.lead_first_name || b.lead_last_name) {
                      who = `${(b.lead_first_name || "").charAt(0)}. ${b.lead_last_name || ""}`.trim();
                    } else if (b.title) {
                      who = b.title;
                    } else if (b.reason) {
                      who = b.reason;
                    } else {
                      who = b.state.replace("_", " ");
                    }
                    const dotColor =
                      b.calendar === "turf_house" ? "bg-emerald-500" :
                      b.calendar === "rrdc_rink" ? "bg-teal-500" :
                      b.calendar === "rrdc_courts" ? "bg-purple-500" :
                      "bg-blue-500"; // rrdc_field (and anything unmapped) = blue
                    return (
                      <div
                        key={b.id}
                        title={`${b.start_time}-${b.end_time} · ${blockLabel(b, true)}`}
                        className="text-[10px] px-1 rounded truncate flex items-center gap-1"
                        style={styles[b.state] || {}}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} />
                        <span className="font-medium shrink-0">{b.start_time}</span>
                        <span className="truncate text-foreground/80">{who}</span>
                      </div>
                    );
                  })}
                  {dayBlocks.length > 3 && <div className="text-xs text-muted-foreground">+{dayBlocks.length - 3}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-outfit text-2xl font-bold tracking-tight">Calendar</h1>
        {(user?.role === "super_admin" || user?.role === "company_admin") && (
          <Button data-testid="add-block-btn" size="sm" onClick={() => setShowBlock(true)}>
            <Plus size={16} /> Block Slot
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <Select value={calendar} onValueChange={setCalendar}>
          <SelectTrigger data-testid="calendar-select" className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            <SelectItem value="turf_house">Turf House</SelectItem>
            <SelectItem value="rrdc_field">RR Field</SelectItem>
            <SelectItem value="rrdc_rink">RR Rink</SelectItem>
            <SelectItem value="rrdc_courts">RR Courts</SelectItem>
          </SelectContent>
        </Select>

        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger data-testid="view-day" value="day">Day</TabsTrigger>
            <TabsTrigger data-testid="view-week" value="week">Week</TabsTrigger>
            <TabsTrigger data-testid="view-month" value="month">Month</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2">
          <Button data-testid="cal-prev" variant="ghost" size="icon" onClick={() => navDate(-1)}>
            <ChevronLeft size={18} />
          </Button>
          <Input
            data-testid="cal-date"
            type="date"
            value={currentDate}
            onChange={(e) => setCurrentDate(e.target.value)}
            className="w-40"
          />
          <Button data-testid="cal-next" variant="ghost" size="icon" onClick={() => navDate(1)}>
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-emerald-500/40" /> Open</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-zinc-500/40" /> Tentative</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-yellow-500/40" /> Reserved</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-500/40" /> Booked</div>
      </div>

      <Card className="border-border">
        <CardContent className="p-4">
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : view === "day" ? (
            <>
              <div className="text-sm font-medium text-muted-foreground mb-3">
                {format(new Date(currentDate + "T00:00:00"), "EEEE, MMMM d, yyyy")} - {calendarLabel(calendar)}
              </div>
              {renderDayView(currentDate)}
            </>
          ) : view === "week" ? (
            renderWeekView()
          ) : (
            <>
              <div className="text-sm font-medium text-muted-foreground mb-3">
                {format(new Date(currentDate + "T00:00:00"), "MMMM yyyy")} - {calendarLabel(calendar)}
              </div>
              {renderMonthView()}
            </>
          )}
        </CardContent>
      </Card>

      {/* Block Dialog */}
      <Dialog open={showBlock} onOpenChange={(v) => { if (!v) setBlockForm({ start_time: "", end_time: "", reason: "", first_name: "", last_name: "", email: "", phone: "" }); setShowBlock(v); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{isWalkIn ? "Walk-in Booking" : "Block Calendar Slot"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {calendar === "all" && (
              <Select value={blockForm.location || ""} onValueChange={(v) => setBlockForm({ ...blockForm, location: v })}>
                <SelectTrigger data-testid="block-location"><SelectValue placeholder="Location (pick one) *" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="turf_house">Turf House</SelectItem>
                  <SelectItem value="rrdc_field">RR Field</SelectItem>
                  <SelectItem value="rrdc_rink">RR Rink</SelectItem>
                  <SelectItem value="rrdc_courts">RR Courts</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={blockForm.reason} onValueChange={(v) => setBlockForm({ ...blockForm, reason: v })}>
              <SelectTrigger data-testid="block-reason"><SelectValue placeholder="Reason" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
                <SelectItem value="Camp Event">Camp Event</SelectItem>
                <SelectItem value="Private Use">Private Use</SelectItem>
                <SelectItem value="Field Rental (walk-in)">Field Rental (walk-in)</SelectItem>
                <SelectItem value="Birthday Party (walk-in)">Birthday Party (walk-in)</SelectItem>
                <SelectItem value="Event (walk-in)">Event (walk-in)</SelectItem>
                <SelectItem value="School Picnic (walk-in)">School Picnic (walk-in)</SelectItem>
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-muted-foreground">Start Time</label>
                <Select value={blockForm.start_time} onValueChange={(v) => setBlockForm({ ...blockForm, start_time: v })}>
                  <SelectTrigger data-testid="block-start"><SelectValue placeholder="Start" /></SelectTrigger>
                  <SelectContent>
                    {slots.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">End Time</label>
                <Select value={blockForm.end_time} onValueChange={(v) => setBlockForm({ ...blockForm, end_time: v })}>
                  <SelectTrigger data-testid="block-end"><SelectValue placeholder="End" /></SelectTrigger>
                  <SelectContent>
                    {slots.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isWalkIn && (
              <div className="space-y-3 pt-2 border-t border-border">
                <p className="text-sm font-medium text-foreground">Client Info</p>
                <div className="grid grid-cols-2 gap-3">
                  <Input data-testid="block-fname" placeholder="First name *" value={blockForm.first_name} onChange={(e) => setBlockForm({ ...blockForm, first_name: e.target.value })} />
                  <Input data-testid="block-lname" placeholder="Last name *" value={blockForm.last_name} onChange={(e) => setBlockForm({ ...blockForm, last_name: e.target.value })} />
                </div>
                <Input data-testid="block-email" placeholder="Email" value={blockForm.email} onChange={(e) => setBlockForm({ ...blockForm, email: e.target.value })} />
                <Input data-testid="block-phone" placeholder="Phone" value={blockForm.phone} onChange={(e) => setBlockForm({ ...blockForm, phone: e.target.value })} />
              </div>
            )}
            {conflictingBlocks.length > 0 && (
              <div data-testid="conflict-warning" className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                      Time conflict — this overlaps {conflictingBlocks.length} existing {conflictingBlocks.length === 1 ? "booking" : "bookings"}
                    </p>
                    <ul className="text-xs text-amber-700 dark:text-amber-300 list-disc pl-4 space-y-0.5">
                      {conflictingBlocks.slice(0, 3).map((b) => (
                        <li key={b.id}>
                          {b.start_time}–{b.end_time} · {blockLabel(b)}
                        </li>
                      ))}
                      {conflictingBlocks.length > 3 && (
                        <li className="opacity-70">…and {conflictingBlocks.length - 3} more</li>
                      )}
                    </ul>
                    <label className="flex items-center gap-2 pt-1 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="conflict-override-checkbox"
                        className="h-4 w-4"
                        checked={!!blockForm.confirm_override}
                        onChange={(e) => setBlockForm({ ...blockForm, confirm_override: e.target.checked })}
                      />
                      <span className="text-xs text-amber-700 dark:text-amber-300">Book anyway (creates an overlapping reservation)</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="block-confirm" onClick={handleCreateBlock}>{isWalkIn ? "Create Booking" : "Block Slot"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
